/**
 * Translation popovers for the reader's "popover" translation style.
 *
 * Declarative replacement for spanTranslatorPopoverLayer (which remains for
 * the browser extension): bubbles render from group state, and an effect
 * measures the translated spans to position them. Clicking a bubble dismisses
 * its translation, mirroring the old dismissSpan behavior.
 */
import { effect, signal } from "@preact/signals";
import * as preact from "preact";

import * as utils from "shared/utils";
import { computePopoverPosition } from "spanTranslatorPopoverLayer";
import type ReaderTextStore from "stores/ReaderTextStore";
import {
  groupTranslationText,
  type ReaderTextGroup,
} from "stores/ReaderTextStore";
import type ReaderViewStore from "stores/ReaderViewStore";

/**
 * Bumped whenever translation popovers should re-measure their position
 * (font/size changes, sidebar transitions, page layout updates). Module-level
 * so settings UI can request a reposition without holding a reference to the
 * reader text store.
 */
const popoverRepositionSequence = signal(0);

export const schedulePopoverReposition = () => {
  // peek() rather than `value += 1`: callers include effects (e.g.
  // FormattingOptions' settings effect), and reading .value there would
  // subscribe the running effect to the signal it is about to write -
  // a self-invalidation that signals-core rejects with "Cycle detected".
  popoverRepositionSequence.value = popoverRepositionSequence.peek() + 1;
};

/**
 * Single-word bubbles are keyed by position so the bubble survives the span
 * being re-keyed; phrase bubbles are keyed by group id.
 */
const keyForGroup = (group: ReaderTextGroup): string =>
  group.from === group.to ? `position-${group.from}` : `group-${group.id}`;

type BubbleViewModel = {
  key: string;
  groupId: number;
  translationText: string;
  transliteration?: string;
  struck: boolean;
};

type BubblePlacement = {
  left: number;
  top: number;
  arrowLeft: number;
  placeBelow: boolean;
  hidden: boolean;
};

type FontStyles = {
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
};

type Props = {
  store: ReaderTextStore;
  readerViewStore?: ReaderViewStore;
};

type State = {
  bubbles: BubbleViewModel[];
  placements: { [key: string]: BubblePlacement };
  layerTop: number;
  fontStyles?: FontStyles;
};

export default class ReaderPopoverLayer extends preact.Component<Props, State> {
  state: State = { bubbles: [], placements: {}, layerTop: 0 };

  private layerElement?: HTMLElement;
  private bubbleElements = new Map<string, HTMLElement>();
  private animationFrame: number | null = null;
  private disposeEffects: Array<() => void> = [];

  componentDidMount() {
    this.disposeEffects.push(
      effect(() => {
        void this.props.store.groups.value;
        void popoverRepositionSequence.value;
        void this.props.readerViewStore?.pageLayoutUpdatedSequence.value;
        this.scheduleReposition();
      })
    );
    window.addEventListener("resize", this.scheduleReposition);
    // Capture phase: in scroll mode (YouTube/audio-synced books) the text
    // scrolls inside #pageTextViewport, and scroll events don't bubble, so a
    // plain window listener never hears them.
    window.addEventListener("scroll", this.scheduleReposition, true);
  }

  componentWillUnmount() {
    for (const dispose of this.disposeEffects) {
      dispose();
    }
    window.removeEventListener("resize", this.scheduleReposition);
    window.removeEventListener("scroll", this.scheduleReposition, true);
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  componentDidUpdate() {
    // A new bubble needs a second pass: render with hidden visibility first so
    // its size can be measured, then place it.
    const needsPlacement = this.state.bubbles.some(
      (bubble) => !this.state.placements[bubble.key]
    );
    if (needsPlacement) {
      this.scheduleReposition();
    }
  }

  render() {
    return (
      <div
        id="readerPopoverLayer"
        class="readerPopoverLayer"
        style={{
          top: `${this.state.layerTop}px`,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        ref={(element) => {
          this.layerElement = element ?? undefined;
        }}
      >
        {this.state.bubbles.map((bubble) => {
          const placement = this.state.placements[bubble.key];
          return (
            <div
              key={bubble.key}
              class={utils.cn({
                readerPopoverBubble: true,
                popoverBelow: Boolean(placement?.placeBelow),
                deleting: bubble.struck,
              })}
              style={{
                // Bubbles stay laid out (display: block) even while hidden, so
                // that measureAndPlace can read their size and place them
                // correctly the moment they scroll back into view.
                display: "block",
                left: `${placement?.left ?? 0}px`,
                top: `${placement?.top ?? 0}px`,
                visibility:
                  placement && !placement.hidden ? "visible" : "hidden",
                "--arrow-left": `${placement?.arrowLeft ?? 0}px`,
                ...this.state.fontStyles,
              }}
              ref={(element) => {
                if (element) {
                  this.bubbleElements.set(bubble.key, element);
                } else {
                  this.bubbleElements.delete(bubble.key);
                }
              }}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                void this.props.store.untranslateGroup(bubble.groupId);
              }}
            >
              {bubble.transliteration !== undefined ? (
                <>
                  <div class="transliteration">{bubble.transliteration}</div>
                  <div>{bubble.translationText}</div>
                </>
              ) : (
                bubble.translationText
              )}
            </div>
          );
        })}
      </div>
    );
  }

  private scheduleReposition = () => {
    if (this.animationFrame !== null) {
      return;
    }
    this.animationFrame = requestAnimationFrame(() => {
      this.animationFrame = null;
      this.measureAndPlace();
    });
  };

  private measureAndPlace() {
    const popoverStyle = document.body.classList.contains("popoverStyle");
    const groups = [...this.props.store.groups.value.values()].filter(
      (group) =>
        group.status === "translated" &&
        !group.monolingual &&
        group.translation !== undefined
    );

    if (!popoverStyle || groups.length === 0) {
      if (this.state.bubbles.length > 0) {
        this.setState({ bubbles: [], placements: {} });
      }
      return;
    }

    const bubbles: BubbleViewModel[] = groups.map((group) => ({
      key: keyForGroup(group),
      groupId: group.id,
      translationText: groupTranslationText(group),
      transliteration: group.transliteration,
      struck: group.struck,
    }));

    const layerTop = (() => {
      const mediaPlayer = document.getElementById("mediaPlayer");
      if (mediaPlayer && mediaPlayer.offsetHeight > 0) {
        return mediaPlayer.getBoundingClientRect().bottom;
      }
      return 0;
    })();
    // Commit the layer's new top before measuring it: bubble positions are
    // stored relative to the layer, so reading a layerRect that still reflects
    // the previous layerTop would offset every placement by the delta (render
    // writes the same value from state, so this is idempotent).
    if (this.layerElement) {
      this.layerElement.style.top = `${layerTop}px`;
    }

    const viewport = document.getElementById("pageTextViewport");
    const viewportRect = viewport?.getBoundingClientRect() ?? {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
    const layerRect = this.layerElement?.getBoundingClientRect();

    const fontStyles = (() => {
      const translationElement = document.querySelector(
        "#pageText .translation"
      );
      if (!translationElement) {
        return undefined;
      }
      const computedStyle = window.getComputedStyle(translationElement);
      return {
        fontFamily: computedStyle.fontFamily,
        fontSize: computedStyle.fontSize,
        fontWeight: computedStyle.fontWeight,
        lineHeight: computedStyle.lineHeight,
      };
    })();

    const placements: { [key: string]: BubblePlacement } = {};
    for (const group of groups) {
      const key = keyForGroup(group);
      const spanElement =
        group.from === group.to
          ? document.querySelector(`#pageText span[data-p="${group.from}"]`)
          : document.querySelector(`#pageText span[data-group="${group.id}"]`);
      const bubbleElement = this.bubbleElements.get(key);
      if (!spanElement || !bubbleElement) {
        continue;
      }

      const spanRect = spanElement.getBoundingClientRect();
      if (
        spanRect.bottom < viewportRect.top ||
        spanRect.top > viewportRect.bottom ||
        spanRect.right < viewportRect.left ||
        spanRect.left > viewportRect.right
      ) {
        placements[key] = {
          left: 0,
          top: 0,
          arrowLeft: 0,
          placeBelow: false,
          hidden: true,
        };
        continue;
      }

      const bubbleRect = bubbleElement.getBoundingClientRect();
      const { left, top, arrowLeft, placeBelow } = computePopoverPosition(
        spanRect,
        spanElement.getClientRects(),
        bubbleRect,
        window.innerWidth,
        layerTop
      );
      placements[key] = {
        left,
        top: top - (layerRect?.top ?? 0),
        arrowLeft,
        placeBelow,
        hidden: false,
      };
    }

    const newState: State = {
      bubbles,
      placements,
      layerTop,
      fontStyles,
    };
    if (JSON.stringify(newState) !== JSON.stringify(this.state)) {
      this.setState(newState);
    }
  }
}
